import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfig } from '@app/config/config.type';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class RuntimeConfigService {
  // Cached configuration object
  // This will be populated after the first loadConfig call
  private config: AppConfig;
  // Base URL components that will be constructed from the loaded configuration
  // The root URL for API requests
  private root: string;
  // The base path for API requests
  private basePath: string;

  constructor(private readonly http: HttpClient) {}

  /**
   * Returns the currently loaded runtime configuration object.
   *
   * @returns The cached config object, or undefined if not yet loaded.
   * @remarks
   * This is a synchronous getter. To ensure config is loaded, use {@link loadConfig} first.
   */
  public get getConfig(): AppConfig | undefined {
    return this.config;
  }

  /**
   * Returns the root URL for API requests.
   *
   * @returns The root URL string.
   */
  public get getRoot(): string {
    return this.root;
  }

  /**
   * Returns the suffix for API requests.
   *
   * @returns The base path string.
   */
  public get getBasePath(): string {
    return this.basePath;
  }

  /**
   * Loads the runtime configuration from a JSON file.
   *
   * @returns A promise that resolves with the loaded configuration object.
   * @remarks
   * This method fetches the configuration from `./assets/data/config.json` and caches it.
   * If the configuration is already loaded, it returns the cached version.
   */
  public loadConfig(): Promise<AppConfig> {
    if (this.config) {
      return Promise.resolve(this.config);
    }
    const datetime = new Date().getTime();
    return firstValueFrom(this.http.get<AppConfig>('./assets/data/config.json?cb=' + datetime)).then((res) => {
      this.config = res;
      return this.config;
    });
  }

  /**
   * Returns the API base URL, loading config if necessary.
   *
   * @param apiRouteScope Optional override for the API route scope.
   * If not provided, it defaults to the external route scope set in the config.
   * @param apiVersion Optional override for the API version.
   * If not provided, it defaults to the v1 version set in the config.
   * @returns A promise that resolves to the API base URL string.
   */
  public getDXAPIBaseUrl(apiRouteScope?: string, apiVersion?: string): string {
    return this.generateDXBaseURL(apiRouteScope, apiVersion);
  }

  /**
   * Generates the base URL for API requests.
   * @param apiRouteScope Optional override for the API route scope.
   * @param apiVersion Optional override for the API version.
   * @returns The generated base URL string.
   */
  private generateDXBaseURL(apiRouteScope?: string, apiVersion?: string): string {
    const apiConfig = this.config?.consumerPortalApi;

    const { isHTTPS, port, domain, suffix, api } = apiConfig || {};
    const protocol = isHTTPS ? 'https://' : 'http://';
    const portPart = port === undefined || port === '' ? '' : `:${port}`;
    const routeScope = apiRouteScope || apiConfig?.routeScope;
    const version = apiVersion || apiConfig?.version;

    this.root = `${protocol}${domain}${portPart}`;

    if (domain?.toString().includes('localhost')) {
      // For local development, use the configured domain and port
      const updatedSuffix = 'cpapi/api';
      this.basePath = updatedSuffix;
      return `${this.root}/${updatedSuffix}`;
    }

    // If any of the required fields are missing, log a warning and return a fallback URL
    if (!routeScope || !version || !api) {
      // Warning that the config is incomplete
      // NOTE: Cannot use SawDust logger here due to circular dependency. Need to dig in deeper to find a solution.
      console.warn('Configuration is missing required fields: routeScope, version, or api.');
      this.basePath = suffix;
      // Fallback: omit missing fields from the URL
      return `${this.root}/${suffix}`;
    }
    this.basePath = `${routeScope}/${suffix}/${version}/${api}`;
    // Construct the full API base URL
    return `${this.root}/${this.basePath}`;
  }
}
